import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { BrandCard } from "../components/BrandCard";
import { Funnel } from "@phosphor-icons/react";

const RISKS = ["All", "low", "medium", "high"];
const ROIS = ["All", "Up to 18%", "18% and above"];
const STAGE_LABEL = { All: "All", low: "Established", medium: "Growing", high: "Emerging" };

export default function Explore() {
  const [brands, setBrands] = useState([]);
  const [industry, setIndustry] = useState("All");
  const [risk, setRisk] = useState("All");
  const [roi, setRoi] = useState("All");
  const [maxTicket, setMaxTicket] = useState(6000000);
  const [sort, setSort] = useState("featured");
  const [applied, setApplied] = useState({ industry: "All", risk: "All", roi: "All", maxTicket: 6000000 });

  useEffect(() => { api.get("/brands").then(({ data }) => setBrands(data)).catch(() => {}); }, []);

  const industries = useMemo(() => ["All", ...Array.from(new Set(brands.map((b) => b.category)))], [brands]);

  const roiFloor = (s) => parseInt(String(s).replace(/[^0-9]/g, "")) || 0;

  const filtered = useMemo(() => {
    let list = brands.filter((b) => {
      const okInd = applied.industry === "All" || b.category === applied.industry;
      const okRisk = applied.risk === "All" || b.risk_level === applied.risk;
      const okTicket = b.ticket_min <= applied.maxTicket;
      const floor = roiFloor(b.expected_roi);
      const okRoi = applied.roi === "All" || (applied.roi === "Up to 18%" ? floor < 18 : floor >= 18);
      return okInd && okRisk && okTicket && okRoi;
    });
    if (sort === "ticket-low") list = [...list].sort((a, b) => a.ticket_min - b.ticket_min);
    if (sort === "ticket-high") list = [...list].sort((a, b) => b.ticket_min - a.ticket_min);
    if (sort === "roi") list = [...list].sort((a, b) => roiFloor(b.expected_roi) - roiFloor(a.expected_roi));
    return list;
  }, [brands, applied, sort]);

  const apply = () => setApplied({ industry, risk, roi, maxTicket });
  const reset = () => { setIndustry("All"); setRisk("All"); setRoi("All"); setMaxTicket(6000000); setApplied({ industry: "All", risk: "All", roi: "All", maxTicket: 6000000 }); };

  const selectCls = "w-full bg-navy-800 border border-white/10 rounded-md px-3 py-2.5 text-sm text-white focus:border-gold focus:outline-none";
  const labelCls = "text-[0.65rem] uppercase tracking-widest text-ebg-muted mb-2 block";

  return (
    <div data-testid="explore-page" className="px-6 md:px-12 lg:px-20 py-12 md:py-16">
      <p className="eyebrow mb-3">Explore Franchise Opportunities</p>
      <h1 className="font-display text-4xl md:text-5xl text-white">Discover franchise opportunities across the EBG ecosystem.</h1>
      <p className="text-ebg-muted mt-3 max-w-2xl">Explore franchise opportunities across multiple industries. Filter by industry, starting range and brand category.</p>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 mt-10">
        {/* Filters */}
        <aside className="lg:col-span-1">
          <div className="bg-navy-900 border border-white/10 rounded-xl p-6 lg:sticky lg:top-24">
            <div className="flex items-center justify-between mb-6">
              <span className="flex items-center gap-2 text-white font-medium"><Funnel size={16} className="text-gold" /> Filter</span>
              <button onClick={reset} data-testid="filter-clear" className="text-xs text-gold hover:underline">Clear All</button>
            </div>
            <div className="space-y-5">
              <div>
                <label className={labelCls}>Industry</label>
                <select value={industry} onChange={(e) => setIndustry(e.target.value)} data-testid="filter-industry" className={selectCls}>
                  {industries.map((i) => <option key={i} value={i} className="bg-navy-800">{i}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Maximum Participation Amount — ₹{(maxTicket / 100000).toFixed(1)}L</label>
                <input type="range" min={500000} max={6000000} step={100000} value={maxTicket} onChange={(e) => setMaxTicket(Number(e.target.value))} data-testid="filter-ticket" className="w-full accent-gold" />
              </div>
              <div>
                <label className={labelCls}>Indicative Payout Range*</label>
                <select value={roi} onChange={(e) => setRoi(e.target.value)} data-testid="filter-roi" className={selectCls}>
                  {ROIS.map((r) => <option key={r} value={r} className="bg-navy-800">{r}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Growth Stage</label>
                <select value={risk} onChange={(e) => setRisk(e.target.value)} data-testid="filter-risk" className={selectCls}>
                  {RISKS.map((r) => <option key={r} value={r} className="bg-navy-800">{STAGE_LABEL[r] || r}</option>)}
                </select>
              </div>
              <button onClick={apply} data-testid="filter-apply" className="w-full btn-gold py-2.5 rounded-md text-sm">Apply Filters</button>
            </div>
          </div>
        </aside>

        {/* Grid */}
        <div className="lg:col-span-3">
          <div className="flex items-center justify-between mb-6">
            <p className="text-sm text-ebg-muted">{filtered.length} opportunities</p>
            <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="explore-sort" className="bg-navy-800 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:border-gold focus:outline-none">
              <option value="featured" className="bg-navy-800">Featured</option>
              <option value="ticket-low" className="bg-navy-800">Participation: Low to High</option>
              <option value="ticket-high" className="bg-navy-800">Participation: High to Low</option>
              <option value="roi" className="bg-navy-800">Highest Payout</option>
            </select>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
            {filtered.map((b) => <BrandCard key={b.slug} brand={b} />)}
          </div>
          {filtered.length === 0 && <p className="text-ebg-muted mt-10">No brands match these filters.</p>}
        </div>
      </div>
    </div>
  );
}
